import React, { useState, useLayoutEffect, useEffect } from "react";
import {
  VStack,
  Box,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Input,
  Button,
  useDisclosure,
  Progress,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import DarkHeader from "src/layout/DarkHeader";
import useLocale from "src/providers/useLocale";
import { LOGIN_ROUTE, PORTAL_ROUTE } from "src/routePaths";
import { Redirect, Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "src/store";
import { update } from "src/store/auth";
import { validateToken } from "src/validateToken";
import AskSalahPlanSelector from "src/components/AskSalahPlanSelector";
import PaymentGatewayModal from "src/components/PaymentGatewayModal/PaymentGatewayModal";
import {
  createAskSalahOrder,
  getUserAskSalahSubscription,
  startAskSalahFreeTrial,
} from "src/api/askSalah";
import { uploadFile } from "src/api";
import type { CreateAskSalahOrderRequest, AskSalahOrderResponse } from "src/types/askSalah";

const AskSalahSubscription = () => {
  const { formatMessage } = useIntl();
  const [locale] = useLocale();
  const toast = useToast();
  const history = useHistory();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);

  const [processing, setProcessing] = useState(false);

  // Ask Salah subscription data
  const [askSalahSubscriptionData, setAskSalahSubscriptionData] = useState<CreateAskSalahOrderRequest>();

  // Gateway selection modal
  const gatewayModalProps = useDisclosure();
  const [selectedGateway, setSelectedGateway] = useState<'tap' | 'myfatoorah' | null>(null);
  const [pendingQuotation, setPendingQuotation] = useState<any>(null);

  // Modal state for file upload (like SAL125)
  const disclosureProps = useDisclosure();
  
  // File upload progress
  const [progress, setProgress] = useState(0);
  
  // Current file name for upload
  const [fileName, setFileName] = useState<string>();
  
  // Success state for manual payments (like SAL125)
  const [success, setSuccess] = useState<boolean>(false);

  useLayoutEffect(() => {
    validateToken();
  }, []);

  useEffect(() => {
    validateToken();
  }, [locale]);

  // File upload handler (exactly like SAL125)
  const handleMedia = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const successCb = (url: string) => {
        if (askSalahSubscriptionData) {
          setAskSalahSubscriptionData({
            ...askSalahSubscriptionData,
            proveUrl: url
          });
        }
      };
      const failureCb = () => {
        toast({
          title: formatMessage({ id: "upload.fail" }),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      };
      
      // Use the same uploadFile function as SAL125
      uploadFile(file, setProgress, successCb, failureCb, user?.accessToken);
    }
  };

  // Open modal for file upload (like SAL125)
  const openModal = (quotation: any) => {
    if (!quotation.selected) return;
    setAskSalahSubscriptionData({
      quotationId: quotation.quotationId,
      planType: quotation.planType,
      billingCycle: quotation.billingCycle,
      paymentMethod: quotation.selected as any,
    });
    setFileName('proveUrl');
    disclosureProps.onOpen();
  };

  //function to handle Ask Salah modal opening
  const handleAskSalahOpening = (quotation: any) => {
    // For free trial, directly subscribe (no gateway selection needed, uses Tap)
    if (quotation.planType === 'trial') {
      subscribeAskSalah({
        quotationId: quotation.quotationId,
        planType: quotation.planType,
        billingCycle: undefined,
        paymentMethod: 'card',
        gateway: 'tap',
      });
      return;
    }
    
    // For card payments, show gateway selection modal
    if (quotation.selected === 'card') {
      setPendingQuotation(quotation);
      gatewayModalProps.onOpen();
      return;
    }
    
    // For bank payment, open modal for file upload
    if (quotation.selected === 'bank') {
      openModal(quotation);
      return;
    }
    
    // For other payment methods, proceed directly
    subscribeAskSalah({
      quotationId: quotation.quotationId,
      planType: quotation.planType,
      billingCycle: quotation.billingCycle,
      paymentMethod: quotation.selected,
    });
  };

  // Handle gateway selection for AskSalah
  const handleAskSalahGatewaySelection = (gateway: 'tap' | 'myfatoorah') => {
    setSelectedGateway(gateway);
    if (pendingQuotation) {
      gatewayModalProps.onClose();
      subscribeAskSalah({
        quotationId: pendingQuotation.quotationId,
        planType: pendingQuotation.planType,
        billingCycle: pendingQuotation.billingCycle,
        paymentMethod: 'card',
        gateway: gateway,
      });
      setPendingQuotation(null);
    }
  };

  // Handle gateway modal close - cleanup state
  const handleAskSalahGatewayModalClose = () => {
    setPendingQuotation(null);
    setSelectedGateway(null);
    setProcessing(false);
    gatewayModalProps.onClose();
  };

  // Ask Salah subscription method (updated to handle manual payments)
  const subscribeAskSalah = (subData: CreateAskSalahOrderRequest) => {
    setAskSalahSubscriptionData(subData);
    if (processing) return;
    setProcessing(true);

    // Instant free trial — update Redux and navigate without full page reload
    if (subData.planType === 'trial') {
      startAskSalahFreeTrial(locale)
        .then(async () => {
          const askSalahSubscription = await getUserAskSalahSubscription(locale);
          if (askSalahSubscription) {
            dispatch(
              update({
                user: { askSalahSubscription } as any,
              }),
            );
          }
          toast({
            title: 'subscribed successfully to AskSalah free trial',
            status: 'success',
            isClosable: true,
          });
          history.push(PORTAL_ROUTE);
        })
        .catch((err) => {
          toast({
            title: formatMessage({ id: 'wentWrong', defaultMessage: 'Something went wrong' }),
            description: err?.message || '',
            status: 'error',
            isClosable: true,
          });
        })
        .finally(() => {
          setProcessing(false);
        });
      return;
    }

    // For card payments: Open window synchronously BEFORE async API call
    // This prevents mobile popup blockers from blocking the window
    let paymentWindow: Window | null = null;
    if (subData.paymentMethod === 'card') {
      paymentWindow = window.open('', '_blank');
      
      // Write loading page to the window
      if (paymentWindow && !paymentWindow.closed) {
        const loadingHTML = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Loading Payment...</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body { 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                height: 100vh; 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              }
              .loader { 
                text-align: center; 
                color: white;
              }
              .spinner {
                border: 4px solid rgba(255, 255, 255, 0.3);
                border-top: 4px solid white;
                border-radius: 50%;
                width: 50px;
                height: 50px;
                animation: spin 1s linear infinite;
                margin: 0 auto 24px;
              }
              .text {
                font-size: 18px;
                font-weight: 500;
                letter-spacing: 0.5px;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p class="text">Opening payment page...</p>
            </div>
          </body>
          </html>
        `;
        paymentWindow.document.write(loadingHTML);
        paymentWindow.document.close();
      }
    }

    // For paid plans: create order using new API
    createAskSalahOrder(subData, locale)
      .then((res: AskSalahOrderResponse) => {
        setProcessing(false);
        if (res && res.id) {
          if (res.url) {
            // Set flag to indicate payment is pending (for auto-refresh on portal)
            sessionStorage.setItem('askSalahPaymentPending', 'true');
            
            // Card payment - redirect the already-opened window to payment gateway
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.location.href = res.url;
            } else {
              // Fallback: if window was blocked or closed, open new one
              window.open(res.url, '_blank');
            }
            
            // Show pending message for card payments
            toast({
              title: formatMessage({ 
                id: 'askSalah.order.created',
                defaultMessage: 'Order Created'
              }),
              description: formatMessage({
                id: 'askSalah.order.pending',
                defaultMessage: 'Please complete the payment to activate your AskSalah subscription.'
              }),
              status: 'info',
              duration: 10000,
              isClosable: true,
            });

            // No need to reload here - user will return to portal and it will auto-refresh
          } else {
            // Manual payment (bank/purchase) - close payment window if opened
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.close();
            }
            // Set success state like SAL125
            setSuccess(true);
          }
        } else {
          // Close payment window on error
          if (paymentWindow && !paymentWindow.closed) {
            paymentWindow.close();
          }
          toast({
            title: formatMessage({ id: 'wentWrong' }),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch((err) => {
        setProcessing(false);
        // Close payment window on error
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        toast({
          title: formatMessage({ id: 'wentWrong' }),
          description: err.message || 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
  };

  if (!user) return <Redirect to={LOGIN_ROUTE} />;

  // Show success message for manual payments (like SAL125)
  if (success) {
    return (
      <>
        <DarkHeader />
        <Box padding="50px 20px 50px 20px">
          <Text color="green.500" textAlign="center">
            <FormattedMessage id="offerPending" />
            <br />
            <a href="https://t.me/SmartsWay125">https://t.me/SmartsWay125</a>
          </Text>
          <Link
            to="/ask-salah-subscription"
            onClick={() => {
              setSuccess(false); // Reset success state to show subscription page
            }}
            style={{
              borderRadius: "5px",
              margin: " 10px auto",
              padding: "5px 10px",
              textAlign: "center",
              display: "block",
              width: "200px",
              color: "white",
              background: "#1f3f93",
            }}
          >
            <FormattedMessage id="changePaymentMethod" />
          </Link>
        </Box>
      </>
    );
  }

  return (
    <>
      <DarkHeader />
      <VStack
        padding="80px 20px"
        display="flex"
        flexDir="column"
        alignItems="center"
        minHeight="100vh"
        backgroundColor="gray.50"
      >
        {/* Page Header */}
        <Box
          width="100%"
          maxWidth="1200px"
          mb={8}
        >
          <VStack spacing={2} mb={8}>
            <Text fontSize="4xl" fontWeight="bold" color="gray.700">
              <FormattedMessage 
                id="askSalah.section.title" 
                defaultMessage="AskSalah Subscription"
              />
            </Text>
            <Text fontSize="lg" color="gray.500" textAlign="center" maxWidth="600px">
              <FormattedMessage 
                id="askSalah.section.subtitle" 
                defaultMessage="Interactive AI powered sessions with Dr Salah Al Rashed"
              />
            </Text>
          </VStack>

          {/* Plan Selector */}
          <AskSalahPlanSelector
            handleOpening={handleAskSalahOpening}
            subscribeAskSalah={subscribeAskSalah}
            processing={processing}
          />
        </Box>
        
        {/* File Upload Modal (like SAL125) */}
        <Modal {...disclosureProps} onClose={disclosureProps.onClose}>
          <ModalOverlay />
          <ModalContent
            width={{ base: "300px", sm: "400px", md: "500px" }}
            minWidth={{ lg: "600px" }}
            marginTop="150px"
          >
            <ModalHeader>
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody>
              <FormControl as="form">
                {processing && (
                  <Box margin="30px 0">
                    <Text color="grey">
                      <FormattedMessage id="wait" />
                    </Text>
                  </Box>
                )}
                {fileName === "proveUrl" && (
                  <>
                    <Text color="grey" fontSize={18} fontWeight="bold">
                      <FormattedMessage 
                        id="askSalah.uploadProof" 
                        defaultMessage="Upload Proof of Payment" 
                      />
                    </Text>
                    <Box margin="30px 0">
                      <Input
                        type="file"
                        required
                        name={fileName}
                        onChange={handleMedia}
                        key={fileName}
                        accept="image/*,.pdf"
                      />
                      {progress > 0 && <Progress value={progress} />}
                    </Box>
                  </>
                )}
                {fileName === "proveUrl" && (
                  <Box textAlign="center" margin="30px 0">
                    <Button
                      backgroundColor="brand.900"
                      color="white"
                      height="50px"
                      _hover={{ filter: "brightness(120%)" }}
                      onClick={() => {
                        if (askSalahSubscriptionData) {
                          subscribeAskSalah(askSalahSubscriptionData);
                          disclosureProps.onClose();
                        }
                      }}
                      disabled={
                        (!askSalahSubscriptionData?.proveUrl &&
                          askSalahSubscriptionData?.paymentMethod === "bank") ||
                        processing
                      }
                    >
                      <FormattedMessage id="subscribe" />
                    </Button>
                  </Box>
                )}
              </FormControl>
            </ModalBody>
          </ModalContent>
        </Modal>
        
        <PaymentGatewayModal
          isOpen={gatewayModalProps.isOpen}
          onClose={handleAskSalahGatewayModalClose}
          onSelect={handleAskSalahGatewaySelection}
        />
      </VStack>
    </>
  );
};

export default AskSalahSubscription;

